import React, { useContext } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LanguageProviderContext } from '@/providers/LanguageProvider';
import { CUSTOMER_IDENTIFICATION_TYPES } from '@/services/hacienda/haciendaHelpers';
import { type CustomerInfo } from '@/types';

interface CustomerIdentityFieldsProps {
  value: CustomerInfo;
  onChange: (patch: Partial<CustomerInfo>) => void;
  error?: string | null;
  showPhone?: boolean;
}

const CustomerIdentityFields: React.FC<CustomerIdentityFieldsProps> = ({
  value,
  onChange,
  error,
  showPhone = false,
}) => {
  const { translator } = useContext(LanguageProviderContext);

  return (
    <div className="space-y-4">
      <div className="grid gap-2">
        <Label htmlFor="customer-identification">
          {translator('Invoices.customerIdentificationLabel')} *
        </Label>
        <Input
          id="customer-identification"
          value={value.identification}
          onChange={(event) => {
            const identification = event.target.value.replace(/[^0-9]/g, '');
            onChange({ identification });
          }}
          required
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="customer-email">{translator('Invoices.customerEmailLabel')} *</Label>
        <Input
          id="customer-email"
          type="email"
          value={value.email}
          onChange={(event) => onChange({ email: event.target.value })}
          required
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="customer-name">{translator('Invoices.customerNameLabel')} *</Label>
        <Input
          id="customer-name"
          value={value.name}
          onChange={(event) => onChange({ name: event.target.value })}
          required
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="customer-type">{translator('Invoices.customerTypeLabel')} *</Label>
        <Select value={value.tipo} onValueChange={(tipo) => onChange({ tipo })}>
          <SelectTrigger id="customer-type">
            <SelectValue placeholder={translator('Invoices.customerIdTypePlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {CUSTOMER_IDENTIFICATION_TYPES.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
      <div className="grid gap-2">
        <Label htmlFor="customer-activity-code">
          {translator('Invoices.customerActivityCodeLabel')}
        </Label>
        <Input
          id="customer-activity-code"
          value={value.activityCode ?? ''}
          onChange={(event) => onChange({ activityCode: event.target.value })}
        />
      </div>
      {showPhone && (
        <div className="grid gap-2">
          <Label htmlFor="customer-phone">{translator('Invoices.customerPhoneLabel')}</Label>
          <Input
            id="customer-phone"
            value={value.phone.number}
            onChange={(event) =>
              onChange({ phone: { ...value.phone, number: event.target.value } })
            }
          />
        </div>
      )}
    </div>
  );
};

export default CustomerIdentityFields;
